import { DeliveryServiceDescriptionModel } from './../../shared/service/model/delivery-service-description.model';
import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';

import { Observable, shareReplay } from 'rxjs';

import { HealthAnalysisRegisterModel } from './journey/model/steps/health-analysis-register-model';
import { environment } from '../../../environments/environment';
import { HealthAnalysisTypeModel } from './type/model/health-analysis-type.model';
import { HealthAnalysisModel } from './journey/model/health-analysis.model';
import { HealthAnalysisCollectedModel } from './journey/model/steps/health-analysis-collected.model';
import { HealthAnalysisReceivedModel } from './journey/model/steps/health-analysis-received.model';
import { HealthAnalysisSentModel } from './journey/model/steps/health-analysis-sent.model';
import { HealthAnalysisSummaryModel } from './journey/model/health-analysis-summary.dto';
import { PaginatedModel } from '../../shared/component/select/pagination/model/paginated.model';
import { HealthAnalysisSampleUploadRequestDTO } from './journey/component/form/file-selector/model/health-analysis-sample-upload-request.model';
import { HealthAnalysisSampleUploadResultModel } from './journey/component/form/file-selector/model/health-analysis-sample-upload-result.model';
import { HealthAnalysisSampleUploadedModel } from './journey/component/form/file-selector/model/health-analysis-sample-uploaded.model';
import { HealthAnalysisTimelineEventModel } from './journey/model/health-analysis-timeline-event.model';
import { HealthAnalysisLabStartedModel } from './journey/model/steps/health-analysis-lab-started.model';
import { HealthAnalysisLabCompletedModel } from './journey/model/steps/health-analysis-lab-completed.model';
import { HealthAnalysisReprocessModel } from './journey/model/steps/health-analysis-reprocess.model';
import { HealthAnalysisRecollectModel } from './journey/model/steps/health-analysis-recollect.model';
import { HealthAnalysisReleaseModel } from './journey/model/steps/health-analysis-release.model';
import { HealthAnalysisUserModel } from './journey/model/health-analysis-user.model';

@Injectable({
    providedIn: 'root'
})
export class HealthAnalysisService {
    BASE = `${environment.HEALTH_ANALYSIS}/health/analysis`;
    BASE_DELIVERY = `${environment.DELIVERY}/delivery`;
    BASE_INSTANCE = `${this.BASE}/journey`;
    BASE_TYPE = `${this.BASE}/type`;
    private rawHttp: HttpClient;

    constructor(private httpService: HttpClient, private handler: HttpBackend) {
        this.rawHttp = new HttpClient(handler);
    }

    public createHealthAnalysis(sample: Partial<HealthAnalysisRegisterModel>): Observable<HealthAnalysisModel | HealthAnalysisModel[]> {
        return this.httpService.post<HealthAnalysisModel | HealthAnalysisModel[]>(`${this.BASE_INSTANCE}`, sample);
    }
    public createHealthAnalysisType(healthAnalysisType: HealthAnalysisTypeModel): Observable<HealthAnalysisTypeModel> {
        return this.httpService.post<HealthAnalysisTypeModel>(`${this.BASE_TYPE}`, healthAnalysisType);
    }
    public deleteHealthAnalysis(healthAnalysisId: string): Observable<void> {
        return this.httpService.delete<void>(`${this.BASE_INSTANCE}/${healthAnalysisId}`);
    }
    public deleteHealthAnalysisInBatch(analysisIdentifiers: Array<string>): Observable<void> {
        return this.httpService.delete<void>(`${this.BASE_INSTANCE}`, { params: { analysisIdentifiers } });
    }
    public deleteHealthAnalysisSampleFile(analysisIdentifier: string, filePath: string): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/${analysisIdentifier}/sample/file/delete`, { externalIdentifier: analysisIdentifier, filePath });
    }
    public deleteHealthAnalysisType(healthAnalysisTypeId: string): Observable<void> {
        return this.httpService.delete<void>(`${this.BASE_TYPE}/${healthAnalysisTypeId}`);
    }
    public getHealthAnalysis(params?: HttpParams): Observable<PaginatedModel<HealthAnalysisModel>> {
        return this.httpService.get<PaginatedModel<HealthAnalysisModel>>(`${this.BASE_INSTANCE}`, { params });
    }
    public getHealthAnalysisThatIsHumanGenome(params?: HttpParams): Observable<PaginatedModel<HealthAnalysisModel>> {
        return this.httpService.get<PaginatedModel<HealthAnalysisModel>>(`${this.BASE_INSTANCE}/genome/human`, { params });
    }
    public getHealthAnalysisSummary(params?: HttpParams): Observable<HealthAnalysisSummaryModel> {
        return this.httpService.get<HealthAnalysisSummaryModel>(`${this.BASE_INSTANCE}/summary`, { params });
    }
    public getHealthAnalysisFileDownloadLink(analysisIdentifier: string, fileName: string): Observable<string> {
        return this.httpService.get(`${this.BASE_INSTANCE}/${analysisIdentifier}/file/${fileName}`, { responseType: 'text' })
    }
    public generateHealthAnalysisSelfOnboardingQRCode(analysisIdentifier: string): Observable<Blob> {
        return this.httpService.get(`${this.BASE_INSTANCE}/${encodeURIComponent(analysisIdentifier)}/qrcode`, { responseType: 'blob' });
    }
    public getHealthAnalysisByAnalysisIdentifier(externalIdentifier: string): Observable<HealthAnalysisModel> {
        return this.httpService.get<HealthAnalysisModel>(`${this.BASE_INSTANCE}/${externalIdentifier}`);
    }
    public getHealthAnalysisTimelineByAnalysisIdentifier(externalIdentifier: string): Observable<Array<HealthAnalysisTimelineEventModel>> {
        return this.httpService.get<Array<HealthAnalysisTimelineEventModel>>(`${this.BASE_INSTANCE}/${externalIdentifier}/timeline`);
    }
    public getHealthAnalysisTypes(): Observable<Array<HealthAnalysisTypeModel>> {
        return this.httpService.get<Array<HealthAnalysisTypeModel>>(`${this.BASE_TYPE}`).pipe(shareReplay(2));
    }
    public getHealthAnalysisUploadUrl(analysisIdentifier: string, uploadDate: HealthAnalysisSampleUploadRequestDTO): Observable<HealthAnalysisSampleUploadResultModel> {
        return this.httpService.post<HealthAnalysisSampleUploadResultModel>(`${this.BASE_INSTANCE}/${analysisIdentifier}/sample/file`, uploadDate);
    }
    public getPrePostingContentDeclaration(trackingCode: string): Observable<string> {
        return this.httpService.get<string>(`${this.BASE_DELIVERY}/content-declaration/${trackingCode}`, { responseType: 'text' as any });
    }
    public getPrePostingLabel(trackingCode: string): Observable<string> {
        return this.httpService.get<string>(`${this.BASE_DELIVERY}/posting/label/${trackingCode}`,  { responseType: 'text' as any });
    }
    public getDeliveryServices(segment?: string): Observable<DeliveryServiceDescriptionModel[]> {
        return this.httpService.get<DeliveryServiceDescriptionModel[]>(`${this.BASE_DELIVERY}/services`, { params: {
            ...(segment && { segment })
        }});
    }
    public reprocessHealthAnalysis(analysisIdentifier: string, reprocess: HealthAnalysisReprocessModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/${analysisIdentifier}/reprocess`, reprocess);
    }
    /**
     * SINGLE
     * */
    public updateHealthAnalysisAsProcessed(analysisIdentifier: string, healthAnalysis: HealthAnalysisLabCompletedModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/${analysisIdentifier}/laboratory-completed`, healthAnalysis);
    }
    /**
     * BATCH
     *
     * */
    public startHealthAnalysisRecollect(analysisIdentifiers: string[], healthAnalysis: HealthAnalysisRecollectModel): Observable<HealthAnalysisModel[]> {
        return this.httpService.patch<HealthAnalysisModel[]>(`${this.BASE_INSTANCE}/recollected`, { ...healthAnalysis, analysisIdentifiers });
    }
    public releaseHealthAnalysis(release: HealthAnalysisReleaseModel): Observable<void> {
        return this.httpService.post<void>(`${this.BASE}/release`, release);
    }
    public updateHealthAnalysisAsSentInBatch(analysisIdentifiers: string[], healthAnalysis: HealthAnalysisSentModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/sent`, { ...healthAnalysis, analysisIdentifiers });
    }
    public updateHealthAnalysisAsCollectedInBatch(analysisIdentifiers: string[], healthAnalysis: HealthAnalysisCollectedModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/collected`, { ...healthAnalysis, analysisIdentifiers });
    }
    public updateHealthAnalysisAsReceivedInBatch(analysisIdentifiers: string[], healthAnalysis: HealthAnalysisReceivedModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/received`, { ...healthAnalysis, analysisIdentifiers });
    }
    public updateHealthAnalysisAsLabStartedInBatch(analysisIdentifiers: string[], healthAnalysis: HealthAnalysisLabStartedModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/laboratory-started`, { ...healthAnalysis, analysisIdentifiers });
    }
    public updateHealthAnalysisSampleAsUploaded(analysisIdentifier: string, healthAnalysisSampleUploaded: HealthAnalysisSampleUploadedModel): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_INSTANCE}/${analysisIdentifier}/sample/file/uploaded`, healthAnalysisSampleUploaded);
    }
    public updateHealthAnalysisType(healthAnalysisId: string, healthAnalysisType: Partial<HealthAnalysisTypeModel>): Observable<void> {
        return this.httpService.patch<void>(`${this.BASE_TYPE}/${healthAnalysisId}`, healthAnalysisType);
    }
    public uploadHealthAnalysisSample(url: string, file: File): Observable<any> {
        return this.rawHttp.put(url, file, {
            headers: {
                'Content-Type': 'application/octet-stream'
            },
            reportProgress: true,
            observe: 'events'
        });
    }
}
